import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "../lib/api";
import { getToken, logout } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { TrendingUp, Users, CreditCard, Trash2, Search, ExternalLink, Download, Database, Pencil, Check, X, Eye, MonitorDot, Smartphone, Loader2, Send, Copy, RefreshCw, AlertTriangle, CheckCircle2, Receipt, FileText, Archive, Bell, BarChart3, Building2, Activity, UserCog, Megaphone, MousePointerClick, KeyRound, ShieldOff } from "lucide-react";

// Map template id → short display name (kept in sync with Dashboard / Landing)
const TEMPLATE_LABEL = {
  neon_pulse: "Créatif",
  aurora: "Agence",
  glass_premium: "Bureautique",
  cyber_grid: "Cyber",
  holographic: "Holographic",
  minimal_lux: "Minimal",
  bento: "Freelance",
  gastronomy: "Gastronomie",
  atelier_wood: "Artisan",
  salon_velvet: "Salon",
  boutique_drop: "Boutique",
  medtech: "Cabinet",
};
const tplLabel = (id) => TEMPLATE_LABEL[id] || id || "—";

const DetailRow = ({ label, value, fullWidth = false, multiline = false, link = null, swatch = false, highlight = null }) => (
  <div className={fullWidth ? "md:col-span-2" : ""}>
    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">{label}</div>
    <div className={`mt-0.5 ${multiline ? "whitespace-pre-line" : "truncate"} ${highlight === "ok" ? "text-emerald-700 font-bold" : highlight === "muted" ? "text-gray-500" : "text-gray-900"} text-sm flex items-center gap-2`}>
      {swatch && value && value.startsWith("#") && (
        <span className="inline-block w-4 h-4 border border-gray-300" style={{ background: value }} />
      )}
      {link ? (
        <a href={link} target="_blank" rel="noreferrer" className="text-[#1E2A5E] hover:underline inline-flex items-center gap-1 truncate">
          {value} <ExternalLink className="w-3 h-3 flex-shrink-0" />
        </a>
      ) : (
        <span>{value}</span>
      )}
    </div>
  </div>
);


const AdminDashboard = () => {
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [gscUrl, setGscUrl] = useState("");
  const [gscResult, setGscResult] = useState(null);
  const [gscLoading, setGscLoading] = useState(false);
  const [gscStatus, setGscStatus] = useState(null);
  const [indexingUrl, setIndexingUrl] = useState("");
  const [indexingLoading, setIndexingLoading] = useState(false);
  const [indexHistory, setIndexHistory] = useState([]);
  const [history, setHistory] = useState([]);
  const [entries, setEntries] = useState([]);
  const [editingSlugId, setEditingSlugId] = useState(null);
  const [editingSlugValue, setEditingSlugValue] = useState("");
  const [bizSearch, setBizSearch] = useState("");
  const [detailsBiz, setDetailsBiz] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [smsTestLoading, setSmsTestLoading] = useState(false);
  const [pubFilter, setPubFilter] = useState("all"); // 'all' | 'published' | 'unpublished'
  // Invoices (Factur-X) admin
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invSearch, setInvSearch] = useState("");
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [bulkInvoiceLoading, setBulkInvoiceLoading] = useState(false);
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState([]);
  // Notifications & reports
  const [events, setEvents] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const [partnerReport, setPartnerReport] = useState(null);
  const [visitsReport, setVisitsReport] = useState(null);
  const [visitsLoading, setVisitsLoading] = useState(false);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const [systemAlertsCounts, setSystemAlertsCounts] = useState({ paid: 0, failed: 0, validation_error: 0, stripe_outage: 0 });
  const [systemAlertsLoading, setSystemAlertsLoading] = useState(false);
  // Password reset requests pending delivery to clients
  const [passwordResets, setPasswordResets] = useState([]);
  const [copiedResetId, setCopiedResetId] = useState(null);
  // Logout all admin devices flow
  const [logoutAllOpen, setLogoutAllOpen] = useState(false);
  const [logoutAllLoading, setLogoutAllLoading] = useState(false);

  const sendTestSms = async () => {
    setSmsTestLoading(true);
    try {
      const res = await api.post("/admin/sms/test");
      toast.success(`SMS de test envoyé${res?.data?.to ? ` à ${res.data.to}` : ""}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Échec de l'envoi du SMS";
      toast.error(typeof msg === "string" ? msg : "Échec de l'envoi du SMS");
    } finally {
      setSmsTestLoading(false);
    }
  };

  const filteredBusinesses = React.useMemo(() => {
    const q = bizSearch.trim().toLowerCase();
    let list = businesses;
    if (pubFilter === "published") list = list.filter((b) => !!b.published);
    else if (pubFilter === "unpublished") list = list.filter((b) => !b.published);
    if (!q) return list;
    return list.filter((b) => {
      const haystack = [
        b.business_name,
        b.email,
        b.owner_email,
        b.phone,
        b.siret,
        b.address,
        b.activity,
        b.slug,
        b.template,
        tplLabel(b.template),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [businesses, bizSearch, pubFilter]);

  const publishedCount = React.useMemo(() => businesses.filter((b) => !!b.published).length, [businesses]);
  const unpublishedCount = businesses.length - publishedCount;

  const allPublished = businesses.length > 0 && businesses.every((b) => b.published);
  const bulkSetPublished = async (next) => {
    setBulkLoading(true);
    try {
      await api.post("/admin/businesses/bulk-publish", { published: next });
      setBusinesses((prev) => prev.map((x) => ({ ...x, published: next })));
      toast.success(next ? "Toutes les pages publiées" : "Toutes les pages dépubliées");
    } catch (e) {
      toast.error("Action collective impossible");
    } finally {
      setBulkLoading(false);
    }
  };

  const togglePublished = async (b) => {
    try {
      const res = await api.patch(`/admin/business/${b.id}`, { published: !b.published });
      setBusinesses((prev) => prev.map((x) => (x.id === b.id ? { ...x, published: res.data.published } : x)));
      toast.success(res.data.published ? "Page publiée" : "Page dépubliée");
    } catch (e) {
      toast.error("Action impossible");
    }
  };

  const startEditSlug = (b) => {
    setEditingSlugId(b.id);
    setEditingSlugValue(b.slug || "");
  };
  const cancelEditSlug = () => {
    setEditingSlugId(null);
    setEditingSlugValue("");
  };
  const saveSlug = async (b) => {
    const newSlug = (editingSlugValue || "").trim();
    if (!newSlug || newSlug === b.slug) {
      cancelEditSlug();
      return;
    }
    try {
      const res = await api.patch(`/admin/business/${b.id}`, { slug: newSlug });
      setBusinesses((prev) => prev.map((x) => (x.id === b.id ? { ...x, slug: res.data.slug } : x)));
      toast.success("URL mise à jour");
      cancelEditSlug();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "URL non valide");
    }
  };

  const downloadExport = async (format) => {  try {
      const token = getToken();
      const res = await fetch(`${API}/admin/export/${format}`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const disposition = res.headers.get("content-disposition") || "";
      const m = disposition.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `pagepro-export.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé`);
    } catch (e) {
      toast.error("Échec de l'export");
    }
  };

  const loadAll = async () => {
    try {
      const [s, b, p, h, e, gs, ih, inv, ev, pc, vr, sa, pr] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/businesses"),
        api.get("/admin/payments"),
        api.get("/admin/gsc/history"),
        api.get("/admin/entries"),
        api.get("/admin/gsc/status").catch(() => ({ data: null })),
        api.get("/admin/gsc/index-history").catch(() => ({ data: [] })),
        api.get("/admin/invoices").catch(() => ({ data: [] })),
        api.get("/admin/events").catch(() => ({ data: { events: [], unread_count: 0 } })),
        api.get("/admin/partner-clicks/report").catch(() => ({ data: null })),
        api.get("/admin/visits/report?days=30").catch(() => ({ data: null })),
        api.get("/admin/alerts/system?limit=100").catch(() => ({ data: null })),
        api.get("/admin/password-resets").catch(() => ({ data: [] })),
      ]);
      setStats(s.data);
      setBusinesses(b.data);
      setPayments(p.data);
      setHistory(h.data);
      setEntries(e.data.entries || []);
      setGscStatus(gs.data);
      setIndexHistory(ih.data || []);
      setInvoices(inv.data || []);
      setEvents(ev.data?.events || []);
      setUnreadCount(ev.data?.unread_count || 0);
      setPartnerReport(pc.data);
      setVisitsReport(vr.data);
      setSystemAlerts(sa.data?.alerts || []);
      setSystemAlertsCounts(sa.data?.counts || { paid: 0, failed: 0, validation_error: 0, stripe_outage: 0 });
      setPasswordResets(Array.isArray(pr.data) ? pr.data : []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        toast.error("Accès admin refusé");
        logout();
        nav("/admin/login");
      }
    }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await api.get("/admin/me");
        if (!alive) return;
        loadAll();
      } catch (e) {
        if (alive) nav("/admin/login");
      }
    })();
    // Poll events every 30s for real-time alerts on account delete/reactivate
    const evInt = setInterval(() => {
      api.get("/admin/events").then((r) => {
        if (!alive) return;
        const newUnread = r.data?.unread_count || 0;
        setUnreadCount((prev) => {
          if (newUnread > prev) {
            const fresh = (r.data?.events || []).find((e) => !e.read);
            if (fresh) {
              const label = fresh.type === "account_deleted" ? "désactivé" : "réactivé";
              toast.message(`🔔 Compte client ${label}`, {
                description: `${fresh.payload?.email || ""} — ${fresh.payload?.business_name || ""}`,
                duration: 8000,
              });
            }
          }
          return newUnread;
        });
        setEvents(r.data?.events || []);
      }).catch(() => {});
    }, 30000);
    return () => { alive = false; clearInterval(evInt); };
    // eslint-disable-next-line
  }, []);

  const markEventsRead = async () => {
    try {
      await api.post("/admin/events/mark-read");
      setUnreadCount(0);
      setEvents((evs) => evs.map((e) => ({ ...e, read: true })));
    } catch (_) {}
  };

  const clearAllEvents = async () => {
    if (!window.confirm("Supprimer toutes les alertes ? Cette action est irréversible.")) return;
    try {
      await api.delete("/admin/events");
      setEvents([]);
      setUnreadCount(0);
      toast.success("Alertes supprimées");
    } catch (_) {
      toast.error("Échec de la suppression");
    }
  };

  const refreshVisitsReport = async () => {
    setVisitsLoading(true);
    try {
      const r = await api.get("/admin/visits/report?days=30");
      setVisitsReport(r.data);
      toast.success("Rapport visiteurs actualisé");
    } catch (_) {
      toast.error("Échec du rafraîchissement");
    } finally {
      setVisitsLoading(false);
    }
  };

  const clearVisitsReport = async () => {
    if (!window.confirm("Supprimer toutes les données de visite et repartir de zéro ?")) return;
    setVisitsLoading(true);
    try {
      await api.delete("/admin/visits");
      const r = await api.get("/admin/visits/report?days=30");
      setVisitsReport(r.data);
      toast.success("Rapport visiteurs réinitialisé");
    } catch (_) {
      toast.error("Échec de la suppression");
    } finally {
      setVisitsLoading(false);
    }
  };

  const refreshSystemAlerts = async () => {
    setSystemAlertsLoading(true);
    try {
      const r = await api.get("/admin/alerts/system?limit=100");
      setSystemAlerts(r.data?.alerts || []);
      setSystemAlertsCounts(r.data?.counts || { paid: 0, failed: 0, validation_error: 0, stripe_outage: 0 });
      toast.success("Alertes système actualisées");
    } catch (_) {
      toast.error("Échec du rafraîchissement");
    } finally {
      setSystemAlertsLoading(false);
    }
  };

  const clearSystemAlerts = async () => {
    if (!window.confirm("Supprimer tout l'historique des alertes système (SMS Stripe & paiements) ? Cette action est irréversible.")) return;
    setSystemAlertsLoading(true);
    try {
      await api.delete("/admin/alerts/system");
      setSystemAlerts([]);
      setSystemAlertsCounts({ paid: 0, failed: 0, validation_error: 0, stripe_outage: 0 });
      toast.success("Historique des alertes effacé");
    } catch (_) {
      toast.error("Échec de la suppression");
    } finally {
      setSystemAlertsLoading(false);
    }
  };

  const deleteBusiness = async (id) => {
    if (!window.confirm("Supprimer définitivement cette entreprise et son compte ?")) return;
    try {
      await api.delete(`/admin/business/${id}`);
      toast.success("Supprimé");
      loadAll();
    } catch (e) {
      toast.error("Échec de la suppression");
    }
  };

  const inspectGSC = async () => {
    if (!gscUrl) return;
    setGscLoading(true);
    setGscResult(null);
    try {
      const res = await api.post("/admin/gsc/inspect-url", { url: gscUrl });
      setGscResult(res.data);
      loadAll();
    } catch (e) {
      toast.error("Échec de l'inspection");
    } finally {
      setGscLoading(false);
    }
  };

  const requestIndexing = async (url, type = "URL_UPDATED") => {
    if (!url) return;
    setIndexingLoading(true);
    try {
      const res = await api.post("/admin/gsc/request-indexing", { url, type });
      toast.success(`Indexation demandée à Google ✓ (${type})`);
      setIndexHistory((prev) => [res.data, ...prev]);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Échec de la demande d'indexation";
      toast.error(typeof msg === "string" ? msg : "Échec de la demande d'indexation");
    } finally {
      setIndexingLoading(false);
    }
  };

  const copyToClipboard = (text, successMsg = "Copié") => {
    navigator.clipboard.writeText(text);
    toast.success(successMsg);
  };

  // ----- Invoices (Factur-X) admin handlers -----
  const downloadInvoice = async (inv) => {
    setDownloadingInvoiceId(inv.id);
    try {
      const res = await api.get(`/admin/invoices/${inv.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = inv.pdf_filename || `facture-${inv.number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Facture ${inv.number} téléchargée`);
    } catch (e) {
      toast.error("Échec du téléchargement");
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const bulkDownloadInvoices = async ({ ids = null, year = null } = {}) => {
    setBulkInvoiceLoading(true);
    try {
      const res = await api.post(
        "/admin/invoices/bulk-download",
        { ids, year },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const ts = new Date().toISOString().slice(0, 10);
      a.download = `factures_export_${ts}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Export ZIP téléchargé");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Échec de l'export groupé");
    } finally {
      setBulkInvoiceLoading(false);
    }
  };

  const toggleSelectInvoice = (id) => {
    setSelectedInvoiceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredInvoices = invoices.filter((inv) => {
    if (!invSearch) return true;
    const q = invSearch.toLowerCase();
    return (
      (inv.number || "").toLowerCase().includes(q) ||
      (inv.customer?.name || "").toLowerCase().includes(q) ||
      (inv.customer?.email || "").toLowerCase().includes(q) ||
      (inv.customer?.siret || "").toLowerCase().includes(q)
    );
  });

  const allFilteredSelected = filteredInvoices.length > 0 && filteredInvoices.every((i) => selectedInvoiceIds.includes(i.id));
  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      setSelectedInvoiceIds((prev) => prev.filter((id) => !filteredInvoices.some((i) => i.id === id)));
    } else {
      setSelectedInvoiceIds((prev) => Array.from(new Set([...prev, ...filteredInvoices.map((i) => i.id)])));
    }
  };

  if (!stats) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-gray-500">Chargement…</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 font-preserve">
      <header className="bg-black text-white">
        <div className="container-pp min-h-16 flex flex-wrap items-center justify-between gap-y-2 py-2 md:py-0">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center shadow-md" data-testid="admin-logo">
              <MonitorDot className="w-5 h-5 text-white" strokeWidth={2.25} />
            </span>
            <div className="font-bold text-xl tracking-tight">Le Guide des Entrepreneurs</div>
            <span className="text-xs uppercase tracking-widest text-orange-400">Admin CMS</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Notifications bell */}
            <div className="relative" data-testid="admin-bell-wrap">
              <Button
                type="button"
                onClick={() => { setBellOpen((v) => !v); if (unreadCount > 0) markEventsRead(); }}
                variant="ghost"
                className="rounded-none text-white hover:bg-transparent hover:text-white relative px-2.5"
                data-testid="admin-bell-btn"
                aria-label="Notifications"
                title="Notifications"
              >
                <Bell className={`w-5 h-5 ${unreadCount > 0 ? "animate-pulse" : ""}`} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-[10px] font-bold flex items-center justify-center rounded-full" data-testid="admin-bell-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </Button>
              {bellOpen && (
                <div className="absolute right-0 top-full mt-1 w-80 sm:w-96 bg-white text-gray-900 shadow-2xl border border-gray-200 z-50 max-h-[480px] overflow-y-auto" data-testid="admin-bell-panel">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                    <div className="font-bold uppercase tracking-widest text-xs text-gray-700">Alertes récentes</div>
                    <button type="button" onClick={() => setBellOpen(false)} className="text-gray-500 hover:text-black text-xs" data-testid="admin-bell-close">Fermer</button>
                  </div>
                  {events.length === 0 ? (
                    <div className="p-6 text-sm text-gray-500 text-center">Aucune alerte récente.</div>
                  ) : (
                    <ul className="divide-y divide-gray-100">
                      {events.slice(0, 20).map((ev) => {
                        const isDel = ev.type === "account_deleted";
                        return (
                          <li key={ev.id} className="p-3 hover:bg-gray-50" data-testid={`admin-event-${ev.id}`}>
                            <div className="flex items-start gap-2.5">
                              <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center ${isDel ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                                {isDel ? <UserCog className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold leading-snug">
                                  Compte client {isDel ? "désactivé" : "réactivé"}
                                </div>
                                <div className="text-xs text-gray-600 truncate">{ev.payload?.email}</div>
                                {ev.payload?.business_name && (
                                  <div className="text-xs text-gray-500 truncate">{ev.payload.business_name}</div>
                                )}
                                <div className="text-[10px] text-gray-400 mt-0.5">{(ev.created_at || "").replace("T", " ").slice(0, 16)}</div>
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <Button
              type="button"
              onClick={sendTestSms}
              disabled={smsTestLoading}
              variant="ghost"
              className="rounded-none text-white hover:bg-transparent hover:text-white font-semibold gap-2"
              data-testid="admin-sms-test-btn"
              title="Envoyer un SMS de test sur la ligne admin"
            >
              {smsTestLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Smartphone className="w-4 h-4" />}
              <span className="hidden sm:inline">Tester l'envoi SMS</span>
            </Button>
            <Button
              onClick={() => setLogoutAllOpen(true)}
              variant="ghost"
              className="rounded-none text-white hover:bg-transparent hover:text-amber-200 font-semibold gap-2"
              data-testid="admin-logout-all-btn"
              title="Déconnecter tous les appareils connectés à l'Admin CMS"
            >
              <ShieldOff className="w-4 h-4" />
              <span className="hidden lg:inline">Déconnecter tous les appareils</span>
              <span className="lg:hidden sm:inline hidden">Tous les appareils</span>
            </Button>
            <Button onClick={async () => { try { await api.post("/admin/auth/logout"); } catch (_) {} logout(); nav("/admin/login"); }} variant="ghost" className="rounded-none text-white hover:bg-transparent hover:text-white font-semibold" data-testid="admin-logout">
              <span className="hidden sm:inline">Déconnexion</span>
              <span className="sm:hidden text-xs">Sortir</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="container-pp py-10">
        {/* Header with subtle tagline */}
        <div className="mb-8 flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.18em] text-orange-600 mb-2">Admin CMS</div>
            <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter">Tableau de bord</h1>
            <p className="text-sm text-gray-600 mt-2 max-w-xl">Pilotage en temps réel : clients, paiements, SEO, facturation, alertes.</p>
          </div>
          <div className="text-xs text-gray-500 flex items-center gap-2 bg-white border border-gray-200 px-3 py-1.5">
            <Activity className="w-3.5 h-3.5 text-emerald-500" />
            <span>Données actualisées toutes les 30 s</span>
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-gray-200 mb-10" data-testid="stats-grid">
          <Card className="rounded-none border-0 bg-white p-5 flex items-center justify-between min-h-[110px]">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Pages publiées</div>
              <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-1.5">{stats.total_pages}</div>
            </div>
            <Building2 className="w-8 h-8 text-[#1E2A5E] flex-shrink-0" />
          </Card>
          <Card className="rounded-none border-0 bg-white p-5 flex items-center justify-between min-h-[110px]">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Clients actifs</div>
              <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-1.5">{stats.active_clients ?? "—"}</div>
              {Number(stats.deactivated_clients) > 0 && (
                <div className="text-[10px] text-red-600 font-semibold mt-0.5">{stats.deactivated_clients} désactivé{stats.deactivated_clients > 1 ? "s" : ""}</div>
              )}
            </div>
            <Users className="w-8 h-8 text-emerald-600 flex-shrink-0" />
          </Card>
          <Card className="rounded-none border-0 bg-white p-5 flex items-center justify-between min-h-[110px]">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Paiements</div>
              <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-1.5">{stats.paid_transactions}</div>
            </div>
            <CreditCard className="w-8 h-8 text-emerald-500 flex-shrink-0" />
          </Card>
          <Card className="rounded-none border-0 bg-white p-5 flex items-center justify-between min-h-[110px]">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Revenus</div>
              <div className="font-heading font-black text-2xl md:text-3xl tracking-tighter mt-1.5 whitespace-nowrap">
                {new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", minimumFractionDigits: 0 }).format(stats.revenue_eur || 0)}
              </div>
            </div>
            <TrendingUp className="w-8 h-8 text-orange-500 flex-shrink-0" />
          </Card>
          <Card className="rounded-none border-0 bg-white p-5 flex items-center justify-between min-h-[110px]">
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-gray-500">Clics partenaires</div>
              <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-1.5">{partnerReport?.totals?.all ?? "—"}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">7 j&nbsp;: <strong className="text-blue-600">{partnerReport?.totals?.["7d"] ?? "—"}</strong></div>
            </div>
            <MousePointerClick className="w-8 h-8 text-violet-500 flex-shrink-0" />
          </Card>
        </div>

        <Tabs defaultValue="businesses">
          <div className="bg-white border border-gray-200 -mb-px overflow-hidden">
            <TabsList className="rounded-none bg-transparent border-0 h-auto p-0 flex flex-nowrap md:flex-wrap overflow-x-auto md:overflow-visible w-full justify-start" data-testid="admin-tabs">
              {[
                { value: "businesses", label: "Entreprises",   Icon: Building2,  testid: "admin-tab-biz" },
                { value: "entries",    label: "Entrées CMS",   Icon: Database,   testid: "admin-tab-entries" },
                { value: "payments",   label: "Paiements",     Icon: CreditCard, testid: "admin-tab-pay" },
                { value: "gsc",        label: "Search Console",Icon: Search,     testid: "admin-tab-gsc" },
                { value: "invoices",   label: "Facture",       Icon: Receipt,    testid: "admin-tab-invoices" },
                { value: "reports",    label: "Rapports & alertes", Icon: BarChart3, testid: "admin-tab-reports" },
              ].map(({ value, label, Icon, testid }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className="group relative flex-shrink-0 rounded-none bg-transparent text-gray-500 hover:bg-gray-100 hover:text-black data-[state=active]:bg-white data-[state=active]:text-black px-4 py-3 md:px-5 md:py-3.5 transition-all duration-200 font-semibold text-sm flex items-center gap-2 border-b-2 border-transparent data-[state=active]:border-black focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black/20"
                  data-testid={testid}
                >
                  <Icon className="w-4 h-4 transition-transform group-data-[state=active]:scale-110" strokeWidth={2.25} />
                  <span className="whitespace-nowrap">{label}</span>
                  {value === "reports" && unreadCount > 0 && (
                    <span className="ml-1 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold flex items-center justify-center rounded-full" data-testid="admin-tab-reports-badge">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="businesses">
            <Card className="rounded-none bg-white border border-gray-200 mt-6 overflow-hidden">
              {/* Publication status filter pills */}
              <div className="flex items-center gap-2 px-4 pt-4 -mb-1" data-testid="pub-filter-bar">
                <span className="text-xs font-bold uppercase tracking-widest text-gray-500 mr-2">Filtre&nbsp;:</span>
                {[
                  { key: "all", label: `Toutes (${businesses.length})`, color: "bg-black text-white" },
                  { key: "published", label: `Publiées (${publishedCount})`, color: "bg-emerald-600 text-white" },
                  { key: "unpublished", label: `Non publiées (${unpublishedCount})`, color: "bg-orange-500 text-white" },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setPubFilter(opt.key)}
                    data-testid={`pub-filter-${opt.key}`}
                    className={`px-3 h-8 text-xs font-bold uppercase tracking-wider border transition ${
                      pubFilter === opt.key
                        ? `${opt.color} border-transparent`
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              {/* Toolbar: search + master toggle */}
              <div className="flex flex-col md:flex-row md:items-center gap-4 px-4 py-4 border-b border-gray-200 bg-gray-50">
                <div className="relative flex-1 max-w-xl">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={bizSearch}
                    onChange={(e) => setBizSearch(e.target.value)}
                    placeholder="Rechercher par SIRET, email, téléphone, adresse, entreprise…"
                    className="pl-9 h-10 rounded-none border-gray-300"
                    data-testid="biz-search-input"
                  />
                </div>
                <div className="flex items-center gap-3 px-3 py-2 bg-white border border-gray-200">
                  <span className="text-xs font-bold uppercase tracking-widest text-gray-600">Publication collective</span>
                  <Switch
                    checked={allPublished}
                    disabled={bulkLoading || businesses.length === 0}
                    onCheckedChange={(v) => bulkSetPublished(!!v)}
                    data-testid="bulk-publish-switch"
                    className="data-[state=checked]:bg-emerald-500"
                  />
                  <span className={`text-xs font-bold ${allPublished ? "text-emerald-600" : "text-gray-500"}`}>
                    {allPublished ? "Toutes publiées" : "Mixte / dépubliées"}
                  </span>
                </div>
              </div>

              <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Entreprise</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Modèle</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Email</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">SIRET</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">URL</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Statut</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinesses.map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-gray-100 hover:bg-blue-50/50 cursor-pointer transition"
                      data-testid={`biz-row-${b.id}`}
                      onClick={(e) => {
                        // Ignore clicks that originate from interactive children
                        const tag = (e.target.tagName || "").toLowerCase();
                        if (["button", "a", "input", "svg", "path"].includes(tag)) return;
                        if (e.target.closest("button, a, input")) return;
                        setDetailsBiz(b);
                      }}
                    >
                      <td className="px-4 py-3 font-semibold">{b.business_name}</td>
                      <td className="px-4 py-3 capitalize">{tplLabel(b.template)}</td>
                      <td className="px-4 py-3 text-gray-600">{b.email}</td>
                      <td className="px-4 py-3 text-gray-600">{b.siret || "—"}</td>
                      <td className="px-4 py-3">
                        {editingSlugId === b.id ? (
                          <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <span className="text-xs text-gray-400 select-none">/entreprise/</span>
                            <Input
                              autoFocus
                              value={editingSlugValue}
                              onChange={(e) => setEditingSlugValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveSlug(b);
                                if (e.key === "Escape") cancelEditSlug();
                              }}
                              className="h-8 rounded-none border-gray-300 text-xs px-2 w-40"
                              data-testid={`slug-input-${b.id}`}
                            />
                            <Button size="sm" variant="ghost" onClick={() => saveSlug(b)} className="rounded-none h-8 px-2 text-emerald-600 hover:bg-emerald-50" data-testid={`slug-save-${b.id}`}>
                              <Check className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={cancelEditSlug} className="rounded-none h-8 px-2 text-gray-500 hover:bg-gray-100" data-testid={`slug-cancel-${b.id}`}>
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <a href={`/entreprise/${b.slug}`} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="text-[#1E2A5E] hover:underline inline-flex items-center gap-1">
                              /entreprise/{b.slug} <ExternalLink className="w-3 h-3" />
                            </a>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); startEditSlug(b); }} className="rounded-none h-7 px-2 text-gray-500 hover:bg-gray-100" data-testid={`slug-edit-${b.id}`} title="Modifier l'URL">
                              <Pencil className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); togglePublished(b); }}
                          data-testid={`toggle-published-${b.id}`}
                          aria-pressed={!!b.published}
                          className={`relative inline-flex items-center gap-2 px-3 h-8 rounded-full border text-xs font-bold uppercase tracking-wider transition ${
                            b.published
                              ? "bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200"
                              : "bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200"
                          }`}
                        >
                          <span
                            className={`inline-block w-2 h-2 rounded-full ${b.published ? "bg-emerald-500" : "bg-gray-400"}`}
                          />
                          {b.published ? "Publiée" : "Dépubliée"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDetailsBiz(b); }} className="rounded-none text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-testid={`view-biz-${b.id}`} title="Voir les informations">
                            <Eye className="w-4 h-4" />
                          </Button>
                          {b.published && b.slug && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.stopPropagation(); requestIndexing(`${window.location.origin}/entreprise/${b.slug}`, "URL_UPDATED"); }}
                              disabled={indexingLoading}
                              className="rounded-none text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                              data-testid={`index-biz-${b.id}`}
                              title="Demander à Google d'indexer cette page"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteBusiness(b.id); }} className="rounded-none text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-biz-${b.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredBusinesses.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">
                      {businesses.length === 0 ? "Aucune entreprise pour le moment." : "Aucun résultat pour cette recherche."}
                    </td></tr>
                  )}
                </tbody>
              </table>
              </div>
            </Card>

            {/* Details dialog */}
            <Dialog open={!!detailsBiz} onOpenChange={(o) => !o && setDetailsBiz(null)}>
              <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="biz-details-dialog">
                {detailsBiz && (
                  <>
                    <DialogHeader>
                      <DialogTitle className="font-heading text-2xl tracking-tight">{detailsBiz.business_name}</DialogTitle>
                      <DialogDescription>
                        Détail complet de la fiche client — modèle&nbsp;
                        <span className="capitalize font-semibold text-gray-800">{tplLabel(detailsBiz.template)}</span>
                      </DialogDescription>
                    </DialogHeader>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm mt-2">
                      <DetailRow label="Statut publication" value={detailsBiz.published ? "Publiée" : "Dépubliée"} highlight={detailsBiz.published ? "ok" : "muted"} />
                      <DetailRow label="URL publique" value={`/entreprise/${detailsBiz.slug}`} link={`/entreprise/${detailsBiz.slug}`} />
                      <DetailRow label="Email contact" value={detailsBiz.email || "—"} />
                      <DetailRow label="Email compte" value={detailsBiz.owner_email || "—"} />
                      <DetailRow label="Téléphone" value={detailsBiz.phone || "—"} />
                      <DetailRow label="SIRET" value={detailsBiz.siret || "—"} />
                      <DetailRow label="Activité" value={detailsBiz.activity || "—"} />
                      <DetailRow label="Adresse" value={detailsBiz.address || "—"} fullWidth />
                      <DetailRow label="Description" value={detailsBiz.description || "—"} fullWidth multiline />
                      <DetailRow label="Plan affiché" value={detailsBiz.show_map ? "Oui" : "Non"} />
                      <DetailRow label="Localisation plan" value={detailsBiz.map_query || "—"} />
                      <DetailRow label="Couleur primaire" value={detailsBiz.primary_color || "—"} swatch />
                      <DetailRow label="Couleur secondaire" value={detailsBiz.secondary_color || "—"} swatch />
                      <DetailRow label="CTA libellé" value={detailsBiz.cta_text || "—"} />
                      <DetailRow label="CTA lien" value={detailsBiz.cta_url || "—"} />
                      <DetailRow label="Logo" value={detailsBiz.logo_url ? "Oui" : "Non"} link={detailsBiz.logo_url || null} />
                      <DetailRow label="Bannière" value={detailsBiz.banner_url ? "Oui" : "Non"} link={detailsBiz.banner_url || null} />
                      <DetailRow
                        label="Galerie"
                        value={
                          Array.isArray(detailsBiz.gallery) && detailsBiz.gallery.length > 0
                            ? `${detailsBiz.gallery.length} image(s)`
                            : "—"
                        }
                      />
                      <DetailRow
                        label="Témoignages"
                        value={`${(detailsBiz.testimonials || []).length} entrée(s)`}
                      />
                      <DetailRow
                        label="Services / Menu"
                        value={`${(detailsBiz.services || []).length} entrée(s)`}
                      />
                      <DetailRow
                        label="Horaires"
                        value={`${(detailsBiz.opening_hours || []).length} jour(s)`}
                      />
                      <DetailRow label="Créée le" value={(detailsBiz.created_at || "").slice(0, 16).replace("T", " ")} />
                      <DetailRow label="Modifiée le" value={(detailsBiz.updated_at || "").slice(0, 16).replace("T", " ")} />
                    </div>

                    {(detailsBiz.opening_hours || []).length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Horaires</h4>
                        <div className="grid grid-cols-2 md:grid-cols-7 gap-2 text-xs">
                          {detailsBiz.opening_hours.map((h) => (
                            <div key={h.day} className="border border-gray-200 px-2 py-1.5">
                              <div className="font-semibold text-gray-800">{h.day}</div>
                              <div className="text-gray-600">
                                {h.closed ? "Fermé" : `${h.open || "—"} → ${h.close || "—"}`}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {(detailsBiz.testimonials || []).length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Témoignages clients</h4>
                        <ul className="space-y-2 text-xs">
                          {detailsBiz.testimonials.map((t, i) => (
                            <li key={i} className="border border-gray-200 px-3 py-2">
                              <div className="font-semibold text-gray-800">{t.author || "Anonyme"} <span className="text-gray-400 font-normal">· {t.rating || 5}★</span></div>
                              <div className="text-gray-600 mt-0.5">{t.text}</div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(detailsBiz.services || []).length > 0 && (
                      <div className="mt-6">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Services / Menu</h4>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                          {detailsBiz.services.map((s, i) => (
                            <li key={i} className="border border-gray-200 px-3 py-2 flex items-start justify-between gap-2">
                              <div>
                                <div className="font-semibold text-gray-800">{s.name}</div>
                                {s.description && <div className="text-gray-600 mt-0.5">{s.description}</div>}
                              </div>
                              {s.price && <div className="font-bold text-gray-900">{s.price}</div>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {detailsBiz.social_links && Object.values(detailsBiz.social_links).some(Boolean) && (
                      <div className="mt-6">
                        <h4 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Réseaux sociaux</h4>
                        <div className="flex flex-wrap gap-2 text-xs">
                          {Object.entries(detailsBiz.social_links).map(([k, v]) =>
                            v ? (
                              <a key={k} href={v} target="_blank" rel="noreferrer" className="border border-gray-200 px-3 py-1.5 capitalize text-blue-600 hover:bg-blue-50">
                                {k}
                              </a>
                            ) : null
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          <TabsContent value="entries">
            <Card className="rounded-none bg-white border border-gray-200 mt-6 p-6 md:p-8">
              <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
                <div>
                  <h3 className="font-heading font-bold text-2xl tracking-tight flex items-center gap-2">
                    <Database className="w-5 h-5" />
                    Entrées CMS — Informations clients
                  </h3>
                  <p className="text-sm text-gray-600 mt-2 max-w-2xl">
                    Toutes les informations saisies par vos clients (nom, SIRET, contacts, horaires, médias…) consolidées et exportables en CSV ou JSON pour intégration externe ou archivage.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button onClick={() => downloadExport("csv")} className="rounded-none bg-[#1E2A5E] hover:bg-[#2FA398] font-semibold" data-testid="export-csv-btn">
                    <Download className="w-4 h-4 mr-2" />
                    CSV
                  </Button>
                  <Button onClick={() => downloadExport("json")} variant="outline" className="rounded-none border-black font-semibold" data-testid="export-json-btn">
                    <Download className="w-4 h-4 mr-2" />
                    JSON
                  </Button>
                </div>
              </div>

              <div className="overflow-x-auto border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr className="text-left">
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Entreprise</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Modèle</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">SIRET</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Téléphone</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Email</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Activité</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Adresse</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Médias</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Paiement</th>
                      <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">URL</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e) => (
                      <tr key={e.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`entry-row-${e.id}`}>
                        <td className="px-3 py-3 font-semibold">{e.business_name}</td>
                        <td className="px-3 py-3 capitalize">{tplLabel(e.template)}</td>
                        <td className="px-3 py-3 text-gray-600">{e.siret || "—"}</td>
                        <td className="px-3 py-3 text-gray-600">{e.phone || "—"}</td>
                        <td className="px-3 py-3 text-gray-600">{e.email || "—"}</td>
                        <td className="px-3 py-3 text-gray-600">{e.activity || "—"}</td>
                        <td className="px-3 py-3 text-gray-600 max-w-xs truncate" title={e.address}>{e.address || "—"}</td>
                        <td className="px-3 py-3 text-gray-600 text-xs">
                          {(e.logo_url ? "Logo " : "") + (e.banner_url ? "Bannière " : "") + (e.gallery ? `Galerie(${e.gallery.split(" | ").filter(Boolean).length})` : "") || "—"}
                        </td>
                        <td className="px-3 py-3">
                          <span className={`text-xs font-bold px-2 py-1 ${e.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
                            {e.payment_status || "—"} {e.payment_amount_eur ? `· ${e.payment_amount_eur}€` : ""}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <a href={e.public_url} target="_blank" rel="noreferrer" className="text-[#1E2A5E] hover:underline inline-flex items-center gap-1 text-xs">
                            {e.public_url} <ExternalLink className="w-3 h-3" />
                          </a>
                        </td>
                      </tr>
                    ))}
                    {entries.length === 0 && (
                      <tr><td colSpan={10} className="px-4 py-10 text-center text-gray-500">Aucune entrée pour le moment.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mt-6 p-4 border border-blue-200 bg-blue-50 text-sm">
                <p className="font-semibold mb-1">À propos des exports</p>
                <p className="text-gray-700">
                  Le fichier <strong>CSV</strong> est compatible Excel/Numbers (encodage UTF-8 avec BOM, séparateur virgule, listes consolidées). Le fichier <strong>JSON</strong> contient l'intégralité des champs structurés (idéal pour import dans un autre CMS ou un pipeline data).
                </p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="payments">
            <Card className="rounded-none bg-white border border-gray-200 mt-6 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Session</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Modèle</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Statut</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Montant</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Email</th>
                    <th className="px-4 py-3 font-semibold uppercase text-xs tracking-widest">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((p) => (
                    <tr key={p.session_id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`pay-row-${p.session_id}`}>
                      <td className="px-4 py-3 font-mono text-xs">{p.session_id.slice(0, 18)}…</td>
                      <td className="px-4 py-3 capitalize">{tplLabel(p.template)}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold px-2 py-1 ${p.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
                          {p.payment_status}
                        </span>
                      </td>
                      <td className="px-4 py-3 font-semibold">{p.amount}€</td>
                      <td className="px-4 py-3 text-gray-600">{p.email || "—"}</td>
                      <td className="px-4 py-3 text-gray-500 text-xs">{(p.created_at || "").slice(0, 16).replace("T", " ")}</td>
                    </tr>
                  ))}
                  {payments.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-10 text-center text-gray-500">Aucun paiement.</td></tr>
                  )}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          <TabsContent value="gsc">
            <Card className="rounded-none bg-white border border-gray-200 mt-6 p-6 md:p-8">
              {/* === Status banner === */}
              <div
                className={`mb-6 p-4 border ${gscStatus?.has_access_to_site ? "border-emerald-300 bg-emerald-50" : gscStatus?.configured ? "border-amber-300 bg-amber-50" : "border-red-300 bg-red-50"}`}
                data-testid="gsc-status-banner"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3">
                    {gscStatus?.has_access_to_site ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    ) : (
                      <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${gscStatus?.configured ? "text-amber-700" : "text-red-700"}`} />
                    )}
                    <div className="text-sm">
                      <p className="font-bold">
                        {gscStatus?.has_access_to_site
                          ? "Google Search Console connecté"
                          : gscStatus?.configured
                          ? "Service Account configuré — accès Search Console à valider"
                          : "Service Account non configuré"}
                      </p>
                      {gscStatus?.service_account_email && (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                          <code className="bg-white border border-gray-300 px-2 py-1 text-xs font-mono break-all">
                            {gscStatus.service_account_email}
                          </code>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => copyToClipboard(gscStatus.service_account_email, "Email du Service Account copié")}
                            className="rounded-none h-7 px-2 text-xs"
                            data-testid="gsc-sa-copy"
                          >
                            <Copy className="w-3 h-3 mr-1" /> Copier
                          </Button>
                        </div>
                      )}
                      {!gscStatus?.has_access_to_site && gscStatus?.configured && (
                        <p className="text-gray-700 mt-2">
                          Pour activer l'<strong>inspection officielle</strong> et la <strong>demande d'indexation</strong>,
                          ajoutez cet email comme <strong>Propriétaire</strong> dans{" "}
                          <a className="underline text-[#1E2A5E]" href="https://search.google.com/search-console/users" target="_blank" rel="noreferrer">
                            Search Console → Paramètres → Utilisateurs et autorisations
                          </a>{" "}
                          de la propriété <code className="bg-white px-1 border">{gscStatus.site_url}</code>.
                        </p>
                      )}
                      {gscStatus?.error && (
                        <p className="text-red-700 text-xs mt-2">Erreur: {gscStatus.error}</p>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={loadAll}
                    className="rounded-none h-8 flex-shrink-0"
                    data-testid="gsc-status-refresh"
                  >
                    <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Actualiser
                  </Button>
                </div>
              </div>

              {/* === 1. Sitemap dynamique (placed BEFORE URL inspection) === */}
              <div className="mb-8 p-5 border border-emerald-200 bg-emerald-50 text-sm" data-testid="sitemap-block">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div>
                    <p className="font-semibold text-base">Sitemap dynamique</p>
                    <p className="text-gray-700 mt-1">
                      Toutes vos pages clients <strong>publiées</strong> sont automatiquement ajoutées au sitemap (<code className="bg-white px-1 py-0.5 border border-emerald-200">{publishedCount}</code> page(s) actuellement). Soumettez l'URL ci-dessous à Search Console pour accélérer l'indexation Google.
                    </p>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                  <code className="flex-1 bg-white border border-emerald-200 px-3 py-2 text-xs break-all font-mono" data-testid="sitemap-url">
                    {`${window.location.origin}/api/sitemap.xml`}
                  </code>
                  <Button
                    onClick={() => copyToClipboard(`${window.location.origin}/api/sitemap.xml`, "URL du sitemap copiée")}
                    className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                    data-testid="sitemap-copy"
                  >
                    <Copy className="w-4 h-4 mr-1.5" /> Copier l'URL
                  </Button>
                  <Button
                    onClick={() => window.open(`${window.location.origin}/api/sitemap.xml`, "_blank", "noopener")}
                    variant="outline"
                    className="rounded-none border-emerald-600 text-emerald-700 hover:bg-emerald-100 font-semibold"
                    data-testid="sitemap-open"
                  >
                    <ExternalLink className="w-4 h-4 mr-1.5" /> Ouvrir
                  </Button>
                </div>
                <p className="text-xs text-gray-600 mt-3">
                  Dans Search Console → Sitemaps → collez l'URL et cliquez « Envoyer ». Google la relira automatiquement chaque fois que vous publierez une nouvelle page.
                </p>
              </div>

              {/* === 2. URL Inspection === */}
              <div className="mb-6">
                <h3 className="font-heading font-bold text-2xl tracking-tight">Inspection d'URL</h3>
                <p className="text-sm text-gray-600 mt-2 max-w-2xl">
                  {gscStatus?.has_access_to_site
                    ? "Inspection officielle via l'API Google Search Console : statut d'indexation, dernière exploration, canonical retenu par Google, ergonomie mobile et résultats enrichis."
                    : "Inspectez une URL pour vérifier son statut HTTP et ses balises SEO. L'inspection officielle Google sera disponible une fois le Service Account ajouté comme Propriétaire dans Search Console."}
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mb-6">
                <Input
                  value={gscUrl}
                  onChange={(e) => setGscUrl(e.target.value)}
                  placeholder="https://leguidedesentrepreneurs.fr/entreprise/votre-slug"
                  className="rounded-none border-gray-300"
                  data-testid="gsc-url-input"
                />
                <Button onClick={inspectGSC} disabled={gscLoading || !gscUrl} className="rounded-none bg-[#1E2A5E] hover:bg-[#2FA398] font-semibold" data-testid="gsc-inspect-btn">
                  <Search className="w-4 h-4 mr-2" />
                  {gscLoading ? "Inspection…" : "Inspecter"}
                </Button>
                <Button
                  onClick={() => requestIndexing(gscUrl, "URL_UPDATED")}
                  disabled={indexingLoading || !gscUrl}
                  className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                  data-testid="gsc-index-btn"
                  title="Demander à Google de (ré)indexer cette URL"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {indexingLoading ? "Envoi…" : "Indexer"}
                </Button>
              </div>

              {gscResult && (
                <div className="border border-gray-200 p-4 bg-gray-50 mb-6" data-testid="gsc-result">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase tracking-widest text-gray-500">
                      Résultat {gscResult.source === "google_api" ? "— API Google Search Console" : "— Vérification HTTP (fallback)"}
                    </div>
                    {gscResult.inspection_link && (
                      <a href={gscResult.inspection_link} target="_blank" rel="noreferrer" className="text-xs text-[#1E2A5E] underline inline-flex items-center gap-1">
                        Ouvrir dans Search Console <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  {gscResult.source === "google_api" ? (
                    <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      <div>
                        <dt className="font-semibold">Verdict global</dt>
                        <dd>
                          <span className={`inline-block px-2 py-0.5 text-xs font-bold ${gscResult.verdict === "PASS" ? "bg-emerald-100 text-emerald-700" : gscResult.verdict === "FAIL" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>
                            {gscResult.verdict || "—"}
                          </span>
                        </dd>
                      </div>
                      <div><dt className="font-semibold">Couverture</dt><dd>{gscResult.coverage_state || "—"}</dd></div>
                      <div><dt className="font-semibold">État d'indexation</dt><dd>{gscResult.indexing_state || "—"}</dd></div>
                      <div><dt className="font-semibold">Robots.txt</dt><dd>{gscResult.robots_txt_state || "—"}</dd></div>
                      <div><dt className="font-semibold">Page récupérée</dt><dd>{gscResult.page_fetch_state || "—"}</dd></div>
                      <div><dt className="font-semibold">Dernière exploration</dt><dd>{gscResult.last_crawl_time ? new Date(gscResult.last_crawl_time).toLocaleString("fr-FR") : "—"}</dd></div>
                      <div><dt className="font-semibold">Explorée comme</dt><dd>{gscResult.crawled_as || "—"}</dd></div>
                      <div><dt className="font-semibold">Ergonomie mobile</dt><dd>{gscResult.mobile_verdict || "—"}</dd></div>
                      <div className="md:col-span-2"><dt className="font-semibold">Canonical Google</dt><dd className="break-all">{gscResult.google_canonical || "—"}</dd></div>
                      <div className="md:col-span-2"><dt className="font-semibold">Canonical utilisateur</dt><dd className="break-all">{gscResult.user_canonical || "—"}</dd></div>
                      {gscResult.sitemap && gscResult.sitemap.length > 0 && (
                        <div className="md:col-span-2">
                          <dt className="font-semibold">Sitemap(s) référençant l'URL</dt>
                          <dd className="break-all text-xs">{gscResult.sitemap.join(", ")}</dd>
                        </div>
                      )}
                    </dl>
                  ) : (
                    <>
                      <dl className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div><dt className="font-semibold">Statut HTTP</dt><dd>{gscResult.status_code || gscResult.error || "—"}</dd></div>
                        <div><dt className="font-semibold">Indexable</dt><dd>{gscResult.indexable ? "Oui" : "Non"}</dd></div>
                        <div className="md:col-span-2"><dt className="font-semibold">URL finale</dt><dd className="break-all">{gscResult.final_url || "—"}</dd></div>
                        <div className="md:col-span-2"><dt className="font-semibold">Title</dt><dd>{gscResult.title || "—"} <span className="text-gray-400 text-xs">({gscResult.title_length || 0} car.)</span></dd></div>
                        <div className="md:col-span-2"><dt className="font-semibold">Description</dt><dd>{gscResult.description || "—"} <span className="text-gray-400 text-xs">({gscResult.description_length || 0} car.)</span></dd></div>
                        <div className="md:col-span-2"><dt className="font-semibold">Canonical</dt><dd className="break-all">{gscResult.canonical || "—"}</dd></div>
                        <div className="md:col-span-2"><dt className="font-semibold">Robots</dt><dd className="break-all font-mono text-xs">{gscResult.robots_meta || "—"}</dd></div>
                      </dl>
                      {gscResult.google_api_error && (
                        <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5">
                          API Google indisponible : {gscResult.google_api_error}
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* === 3. Quick-index your published pages === */}
              {businesses && businesses.filter((b) => b.published && b.slug).length > 0 && (
                <div className="mb-6 p-4 border border-gray-200 bg-white">
                  <p className="font-semibold text-sm mb-3">Indexation rapide des pages publiées</p>
                  <div className="max-h-56 overflow-y-auto border border-gray-100">
                    {businesses
                      .filter((b) => b.published && b.slug)
                      .map((b) => {
                        const url = `${window.location.origin}/entreprise/${b.slug}`;
                        return (
                          <div key={b.id} className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100 text-xs">
                            <span className="truncate flex-1">
                              <strong className="text-gray-900">{b.business_name || b.slug}</strong>{" "}
                              <span className="text-gray-500 break-all">/entreprise/{b.slug}</span>
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => { setGscUrl(url); inspectGSC(); }}
                              className="rounded-none h-7 px-2 text-xs"
                              data-testid={`quick-inspect-${b.slug}`}
                            >
                              <Search className="w-3 h-3 mr-1" /> Inspecter
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => requestIndexing(url, "URL_UPDATED")}
                              disabled={indexingLoading}
                              className="rounded-none h-7 px-2 text-xs bg-emerald-600 hover:bg-emerald-700"
                              data-testid={`quick-index-${b.slug}`}
                            >
                              <Send className="w-3 h-3 mr-1" /> Indexer
                            </Button>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* === 4. Histories === */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">Historique des inspections</div>
                  <div className="border border-gray-200 max-h-64 overflow-y-auto">
                    {history.length === 0 && <div className="p-4 text-center text-sm text-gray-500">Aucun historique.</div>}
                    {history.map((h) => (
                      <div key={h.id || h.url} className="px-4 py-2 border-b border-gray-100 text-sm flex items-center justify-between gap-3">
                        <span className="truncate flex-1" title={h.url}>{h.url}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 ${h.indexable ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                          {h.verdict || h.status_code || "ERR"}
                        </span>
                        <span className="text-xs text-gray-400 hidden md:inline">{(h.checked_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <div className="text-xs uppercase tracking-widest text-gray-500 mb-3">Historique des demandes d'indexation</div>
                  <div className="border border-gray-200 max-h-64 overflow-y-auto" data-testid="index-history">
                    {indexHistory.length === 0 && <div className="p-4 text-center text-sm text-gray-500">Aucune demande envoyée.</div>}
                    {indexHistory.map((h) => (
                      <div key={h.id || (h.url + h.submitted_at)} className="px-4 py-2 border-b border-gray-100 text-sm flex items-center justify-between gap-3">
                        <span className="truncate flex-1" title={h.url}>{h.url}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 ${h.type === "URL_DELETED" ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                          {h.type || "URL_UPDATED"}
                        </span>
                        <span className="text-xs text-gray-400 hidden md:inline">{(h.submitted_at || "").slice(0, 16).replace("T", " ")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8 p-4 border border-blue-200 bg-blue-50 text-sm">
                <p className="font-semibold mb-1">À propos de l'API d'indexation Google</p>
                <p className="text-gray-700">
                  L'API « Indexing » est officiellement destinée aux pages structurées (offres d'emploi, événements en direct). Pour les autres pages,
                  Google peut accepter la demande mais ne garantit pas l'indexation. Combinez avec la soumission du sitemap pour de meilleurs résultats.
                </p>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="invoices">
            <Card className="rounded-none bg-white border border-gray-200 mt-6 p-6 md:p-8" data-testid="admin-invoices-card">
              <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt className="w-5 h-5 text-[#1E2A5E]" />
                    <h2 className="font-heading font-bold text-2xl tracking-tight">Facture électronique</h2>
                  </div>
                  <p className="text-sm text-gray-600 max-w-2xl">
                    Toutes les factures clients au format <strong>Factur-X</strong> (PDF/A-3 + XML CII MINIMUM, conforme à la facturation électronique française).
                    Numérotation séquentielle annuelle. TVA non applicable&nbsp;— art. 293 B du CGI.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => bulkDownloadInvoices({ year: new Date().getFullYear() })}
                    disabled={bulkInvoiceLoading || invoices.length === 0}
                    className="rounded-none bg-[#1E2A5E] hover:bg-[#2FA398] text-white font-semibold"
                    data-testid="bulk-invoices-year"
                  >
                    <Archive className="w-4 h-4 mr-1.5" />
                    {bulkInvoiceLoading ? "Préparation…" : `ZIP année ${new Date().getFullYear()}`}
                  </Button>
                  <Button
                    onClick={() => bulkDownloadInvoices({})}
                    disabled={bulkInvoiceLoading || invoices.length === 0}
                    variant="outline"
                    className="rounded-none border-black font-semibold"
                    data-testid="bulk-invoices-all"
                  >
                    <Download className="w-4 h-4 mr-1.5" />
                    Tout exporter
                  </Button>
                  {selectedInvoiceIds.length > 0 && (
                    <Button
                      onClick={() => bulkDownloadInvoices({ ids: selectedInvoiceIds })}
                      disabled={bulkInvoiceLoading}
                      className="rounded-none bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                      data-testid="bulk-invoices-selected"
                    >
                      <Archive className="w-4 h-4 mr-1.5" />
                      Sélection ({selectedInvoiceIds.length})
                    </Button>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <Input
                  value={invSearch}
                  onChange={(e) => setInvSearch(e.target.value)}
                  placeholder="Rechercher par numéro, client, SIRET, email…"
                  className="rounded-none border-gray-300"
                  data-testid="admin-invoices-search"
                />
              </div>

              {invoices.length === 0 ? (
                <div className="text-center py-12 border border-dashed border-gray-300 bg-gray-50" data-testid="admin-invoices-empty">
                  <FileText className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 text-sm">Aucune facture émise pour le moment.</p>
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr className="text-left">
                        <th className="px-3 py-3 w-10">
                          <input
                            type="checkbox"
                            checked={allFilteredSelected}
                            onChange={toggleSelectAllFiltered}
                            data-testid="invoice-select-all"
                            aria-label="Tout sélectionner"
                          />
                        </th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">N° Facture</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Date</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Client</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">SIRET</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest">Montant TTC</th>
                        <th className="px-3 py-3 font-semibold uppercase text-xs tracking-widest text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredInvoices.map((inv) => (
                        <tr key={inv.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`admin-invoice-row-${inv.number}`}>
                          <td className="px-3 py-3">
                            <input
                              type="checkbox"
                              checked={selectedInvoiceIds.includes(inv.id)}
                              onChange={() => toggleSelectInvoice(inv.id)}
                              data-testid={`invoice-select-${inv.number}`}
                              aria-label={`Sélectionner ${inv.number}`}
                            />
                          </td>
                          <td className="px-3 py-3 font-mono font-semibold text-gray-900">{inv.number}</td>
                          <td className="px-3 py-3 text-gray-600 text-xs">{(inv.issued_at || "").slice(0, 10)}</td>
                          <td className="px-3 py-3">
                            <div className="font-medium text-gray-900">{inv.customer?.name || "—"}</div>
                            <div className="text-xs text-gray-500">{inv.customer?.email || ""}</div>
                          </td>
                          <td className="px-3 py-3 text-gray-600 font-mono text-xs">{inv.customer?.siret || "—"}</td>
                          <td className="px-3 py-3 font-semibold">{Number(inv.amount_ttc || 0).toFixed(2).replace(".", ",")} €</td>
                          <td className="px-3 py-3 text-right">
                            <div className="inline-flex items-center gap-2 justify-end flex-wrap">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`${API}/admin/invoices/${inv.id}/view`, "_blank", "noopener,noreferrer")}
                                className="rounded-none border-gray-400 text-gray-800 hover:bg-gray-100 font-semibold"
                                data-testid={`admin-view-invoice-${inv.number}`}
                                title="Ouvrir le PDF dans un nouvel onglet"
                              >
                                <Eye className="w-4 h-4 mr-1.5" />
                                Voir
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => downloadInvoice(inv)}
                                disabled={downloadingInvoiceId === inv.id}
                                className="rounded-none bg-[#1E2A5E] hover:bg-[#2FA398] text-white font-semibold"
                                data-testid={`admin-download-invoice-${inv.number}`}
                              >
                                <Download className="w-4 h-4 mr-1.5" />
                                {downloadingInvoiceId === inv.id ? "…" : "PDF"}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {filteredInvoices.length === 0 && (
                        <tr><td colSpan={7} className="px-4 py-10 text-center text-gray-500">Aucun résultat.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="reports">
            <div className="space-y-6 mt-6">
              {/* Site visits report (marketing pages on leguidedesentrepreneurs.fr) */}
              <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8" data-testid="visits-report-card">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Activity className="w-5 h-5 text-emerald-600" />
                      <h2 className="font-heading font-bold text-2xl tracking-tight">Visiteurs du site (30 jours)</h2>
                    </div>
                    <p className="text-sm text-gray-600 max-w-3xl">
                      Trafic sur <strong>leguidedesentrepreneurs.fr</strong> et <strong>www.leguidedesentrepreneurs.fr</strong>. Les visiteurs sont comptés de manière anonyme (IP hashée côté serveur).
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      onClick={refreshVisitsReport}
                      disabled={visitsLoading}
                      variant="outline"
                      className="rounded-none border-emerald-600 text-emerald-700 hover:bg-emerald-50 font-semibold"
                      data-testid="visits-refresh"
                    >
                      <RefreshCw className={`w-4 h-4 mr-1.5 ${visitsLoading ? "animate-spin" : ""}`} />
                      Rafraîchir
                    </Button>
                    <Button
                      onClick={clearVisitsReport}
                      disabled={visitsLoading}
                      variant="outline"
                      className="rounded-none border-red-600 text-red-700 hover:bg-red-50 font-semibold"
                      data-testid="visits-clear"
                    >
                      <Trash2 className="w-4 h-4 mr-1.5" />
                      Supprimer
                    </Button>
                  </div>
                </div>

                {!visitsReport ? (
                  <div className="text-sm text-gray-500 py-6 text-center" data-testid="visits-loading">Chargement…</div>
                ) : (
                  <>
                    {/* KPIs row */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 mb-6">
                      <div className="bg-white p-4">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Visites totales</div>
                        <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter text-emerald-700" data-testid="visits-kpi-total">
                          {visitsReport.total || 0}
                        </div>
                      </div>
                      <div className="bg-white p-4">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Visiteurs uniques</div>
                        <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter text-emerald-700" data-testid="visits-kpi-unique">
                          {visitsReport.unique || 0}
                        </div>
                      </div>
                      {Object.entries(visitsReport.per_host || {}).map(([host, v]) => (
                        <div key={host} className="bg-white p-4">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 truncate">{host}</div>
                          <div className="font-heading font-black text-2xl md:text-3xl tracking-tighter text-gray-900">
                            {v.total || 0}
                            <span className="text-sm font-semibold text-gray-500 ml-1">visites</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">{v.unique || 0} uniques</div>
                        </div>
                      ))}
                    </div>

                    {/* Top referrers & paths */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Sources de trafic (top 5)</div>
                        {(visitsReport.top_referrers || []).length === 0 ? (
                          <div className="text-sm text-gray-500 italic">Aucune donnée</div>
                        ) : (
                          <ul className="divide-y divide-gray-100 border border-gray-200">
                            {(visitsReport.top_referrers || []).map((r, i) => (
                              <li key={`ref-${i}`} className="px-3 py-2 flex items-center justify-between text-sm">
                                <span className="truncate text-gray-800" title={r.value}>{r.value}</span>
                                <span className="font-bold text-emerald-700 ml-2">{r.count}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <div>
                        <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-2">Pages les plus vues (top 5)</div>
                        {(visitsReport.top_paths || []).length === 0 ? (
                          <div className="text-sm text-gray-500 italic">Aucune donnée</div>
                        ) : (
                          <ul className="divide-y divide-gray-100 border border-gray-200">
                            {(visitsReport.top_paths || []).map((r, i) => (
                              <li key={`pa-${i}`} className="px-3 py-2 flex items-center justify-between text-sm">
                                <span className="truncate text-gray-800 font-mono" title={r.value}>{r.value}</span>
                                <span className="font-bold text-emerald-700 ml-2">{r.count}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {visitsReport.generated_at && (
                      <div className="text-[11px] text-gray-400 mt-4">
                        Généré le {visitsReport.generated_at.replace("T", " ").slice(0, 16)} (UTC)
                      </div>
                    )}
                  </>
                )}
              </Card>

              {/* Partner clicks report */}
              <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8" data-testid="partner-report-card">
                <div className="flex items-center gap-2 mb-2">
                  <MousePointerClick className="w-5 h-5 text-violet-600" />
                  <h2 className="font-heading font-bold text-2xl tracking-tight">Rapport — Clics sur partenaires</h2>
                </div>
                <p className="text-sm text-gray-600 max-w-3xl mb-6">
                  Nombre de clics depuis l'<strong>onglet « Facture »</strong> des espaces clients vers les outils partenaires de facturation.
                </p>
                {!partnerReport ? (
                  <div className="text-sm text-gray-500 py-6 text-center">Chargement…</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-200">
                    {[
                      { key: "dougs", label: "dougs.fr", subtitle: "Facture électronique agréée", url: "https://www.dougs.fr/logiciel-facturation-gratuit/", color: "#1E2A5E" },
                      { key: "mafactureentreprise", label: "MaFactureEntreprise.fr", subtitle: "Générateur compatible", url: "https://www.mafactureentreprise.fr", color: "#059669" },
                    ].map((p) => {
                      const data = partnerReport.partners?.[p.key] || { total: 0, last_30d: 0, last_7d: 0, last_click_at: null };
                      return (
                        <div key={p.key} className="bg-white p-5" data-testid={`partner-card-${p.key}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-[0.18em] mb-0.5" style={{ color: p.color }}>{p.subtitle}</div>
                              <div className="font-heading font-bold text-lg text-gray-900">{p.label}</div>
                              <a href={p.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-500 hover:text-gray-900 underline inline-flex items-center gap-1 mt-0.5">
                                Ouvrir <ExternalLink className="w-3 h-3" />
                              </a>
                            </div>
                            <div className="text-right">
                              <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter" style={{ color: p.color }} data-testid={`partner-total-${p.key}`}>{data.total}</div>
                              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">clics au total</div>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-gray-100">
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">30 jours</div>
                              <div className="font-bold text-lg">{data.last_30d}</div>
                            </div>
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">7 jours</div>
                              <div className="font-bold text-lg">{data.last_7d}</div>
                            </div>
                          </div>
                          <div className="text-[11px] text-gray-500 mt-3">
                            Dernier clic&nbsp;: {data.last_click_at ? (data.last_click_at.replace("T", " ").slice(0, 16)) : "—"}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card>

              {/* System alerts log (admin SMS history: paid/failed/validation_error/stripe_outage) */}
              <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8" data-testid="system-alerts-card">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="w-5 h-5 text-red-600" />
                      <h2 className="font-heading font-bold text-2xl tracking-tight">Alertes système</h2>
                    </div>
                    <p className="text-sm text-gray-600 max-w-3xl">
                      Historique des SMS envoyés à l'admin&nbsp;: nouvelles commandes payées, paiements refusés, erreurs Stripe, pannes du service de paiement. Idéal pour repérer un incident en un coup d'œil.
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      onClick={refreshSystemAlerts}
                      disabled={systemAlertsLoading}
                      variant="outline"
                      className="rounded-none border-gray-900 text-gray-900 hover:bg-gray-50 font-semibold"
                      data-testid="system-alerts-refresh"
                    >
                      <RefreshCw className={`w-4 h-4 mr-1.5 ${systemAlertsLoading ? "animate-spin" : ""}`} />
                      Rafraîchir
                    </Button>
                    {systemAlerts.length > 0 && (
                      <Button
                        onClick={clearSystemAlerts}
                        disabled={systemAlertsLoading}
                        variant="outline"
                        className="rounded-none border-red-600 text-red-700 hover:bg-red-50 font-semibold"
                        data-testid="system-alerts-clear"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Tout supprimer
                      </Button>
                    )}
                  </div>
                </div>

                {/* KPIs row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-gray-200 mb-6">
                  <div className="bg-white p-4" data-testid="alert-kpi-paid">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Payés</div>
                    <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter text-emerald-700">{systemAlertsCounts.paid || 0}</div>
                  </div>
                  <div className="bg-white p-4" data-testid="alert-kpi-failed">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Refusés</div>
                    <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter text-orange-600">{systemAlertsCounts.failed || 0}</div>
                  </div>
                  <div className="bg-white p-4" data-testid="alert-kpi-validation">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Validation</div>
                    <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter text-amber-600">{systemAlertsCounts.validation_error || 0}</div>
                  </div>
                  <div className="bg-white p-4" data-testid="alert-kpi-outage">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Panne Stripe</div>
                    <div className="font-heading font-black text-3xl md:text-4xl tracking-tighter text-red-700">{systemAlertsCounts.stripe_outage || 0}</div>
                  </div>
                </div>

                {systemAlerts.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-gray-300 bg-gray-50" data-testid="system-alerts-empty">
                    <Bell className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">Aucune alerte enregistrée.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-gray-200">
                    <table className="w-full text-sm" data-testid="system-alerts-table">
                      <thead className="bg-gray-50 text-left text-[10px] font-bold uppercase tracking-widest text-gray-500">
                        <tr>
                          <th className="px-3 py-2.5">Type</th>
                          <th className="px-3 py-2.5">Description</th>
                          <th className="px-3 py-2.5 hidden md:table-cell">Client / Session</th>
                          <th className="px-3 py-2.5 hidden lg:table-cell">Montant</th>
                          <th className="px-3 py-2.5">Statut</th>
                          <th className="px-3 py-2.5 text-right">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {systemAlerts.map((a) => {
                          const styles = {
                            paid:              { bg: "bg-emerald-100", txt: "text-emerald-700", chip: "Payé" },
                            failed:            { bg: "bg-orange-100",  txt: "text-orange-700",  chip: "Refusé" },
                            validation_error:  { bg: "bg-amber-100",   txt: "text-amber-700",   chip: "Validation" },
                            stripe_outage:     { bg: "bg-red-100",     txt: "text-red-700",     chip: "Panne Stripe" },
                          }[a.kind] || { bg: "bg-gray-100", txt: "text-gray-700", chip: a.kind };
                          const sent = a.status === "sent";
                          return (
                            <tr key={a.id} className="hover:bg-gray-50" data-testid={`system-alert-row-${a.id}`}>
                              <td className="px-3 py-2.5 align-top">
                                <span className={`inline-block text-[10px] font-bold px-2 py-0.5 uppercase tracking-wider ${styles.bg} ${styles.txt}`}>
                                  {styles.chip}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 align-top">
                                <div className="text-sm font-semibold text-gray-900 truncate max-w-xs">{a.label || "—"}</div>
                                {a.detail && (
                                  <div className="text-[11px] text-gray-500 truncate max-w-xs" title={a.detail}>{a.detail}</div>
                                )}
                              </td>
                              <td className="px-3 py-2.5 align-top hidden md:table-cell">
                                <div className="text-xs text-gray-700 truncate max-w-[180px]">{a.email || "—"}</div>
                                {a.session_id && (
                                  <div className="text-[10px] text-gray-400 font-mono truncate max-w-[180px]">{a.session_id.slice(0, 24)}…</div>
                                )}
                              </td>
                              <td className="px-3 py-2.5 align-top hidden lg:table-cell text-sm text-gray-700">
                                {a.amount ? `${Number(a.amount).toFixed(2)} ${a.currency || "EUR"}` : "—"}
                              </td>
                              <td className="px-3 py-2.5 align-top">
                                <span className={`inline-flex items-center gap-1 text-xs font-semibold ${sent ? "text-emerald-700" : "text-red-700"}`}>
                                  {sent ? <CheckCircle2 className="w-3.5 h-3.5" /> : <X className="w-3.5 h-3.5" />}
                                  {sent ? "Envoyé" : "Échec"}
                                </span>
                              </td>
                              <td className="px-3 py-2.5 align-top text-right text-xs text-gray-500 whitespace-nowrap">
                                {(a.created_at || "").replace("T", " ").slice(0, 16)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Pending password reset requests — admin relays temp passwords to clients */}
              <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8" data-testid="password-resets-card">
                <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <KeyRound className="w-5 h-5 text-amber-600" />
                      <h2 className="font-heading font-bold text-2xl tracking-tight">Mots de passe oubliés</h2>
                      {passwordResets.length > 0 && (
                        <span className="ml-1 px-2 py-0.5 text-xs font-bold bg-amber-600 text-white rounded-none" data-testid="password-resets-count">
                          {passwordResets.length}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 max-w-3xl">
                      Demandes de mot de passe oublié en attente de transmission au client.
                      Copiez le mot de passe provisoire et transmettez-le au client par le canal de votre choix
                      (téléphone, SMS, email externe). Une fois le client reconnecté et son mot de passe modifié,
                      l'entrée est automatiquement retirée de cette liste.
                    </p>
                  </div>
                  <Button
                    onClick={loadAll}
                    variant="outline"
                    className="rounded-none border-gray-900 text-gray-900 hover:bg-gray-50 font-semibold"
                    data-testid="password-resets-refresh"
                  >
                    <RefreshCw className="w-4 h-4 mr-1.5" />
                    Rafraîchir
                  </Button>
                </div>

                {passwordResets.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-gray-300 bg-gray-50" data-testid="password-resets-empty">
                    <KeyRound className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">Aucune demande de mot de passe en attente.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-gray-200">
                    <table className="w-full text-sm" data-testid="password-resets-table">
                      <thead className="bg-amber-50 text-left text-[10px] font-bold uppercase tracking-widest text-amber-900">
                        <tr>
                          <th className="px-3 py-2.5">Email client</th>
                          <th className="px-3 py-2.5 hidden md:table-cell">Entreprise</th>
                          <th className="px-3 py-2.5">Mot de passe provisoire</th>
                          <th className="px-3 py-2.5 hidden md:table-cell">Demandé le</th>
                          <th className="px-3 py-2.5 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {passwordResets.map((r) => (
                          <tr key={r.id} className="hover:bg-amber-50/50" data-testid={`password-reset-row-${r.id}`}>
                            <td className="px-3 py-3 font-medium text-gray-900 break-all">{r.email}</td>
                            <td className="px-3 py-3 text-gray-700 hidden md:table-cell">{r.business_name || "—"}</td>
                            <td className="px-3 py-3">
                              <div className="flex items-center gap-2">
                                <code
                                  className="font-mono font-bold text-amber-900 bg-amber-100 px-2 py-1 select-all"
                                  data-testid={`password-reset-value-${r.id}`}
                                >
                                  {r.temp_password}
                                </code>
                                <Button
                                  onClick={async () => {
                                    try {
                                      await navigator.clipboard.writeText(r.temp_password);
                                      setCopiedResetId(r.id);
                                      toast.success("Mot de passe copié");
                                      setTimeout(() => setCopiedResetId((cur) => (cur === r.id ? null : cur)), 1800);
                                    } catch {
                                      toast.error("Copie impossible");
                                    }
                                  }}
                                  variant="ghost"
                                  size="sm"
                                  className="rounded-none px-2 h-8"
                                  data-testid={`password-reset-copy-${r.id}`}
                                >
                                  {copiedResetId === r.id ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
                                </Button>
                              </div>
                            </td>
                            <td className="px-3 py-3 text-gray-600 hidden md:table-cell whitespace-nowrap">
                              {new Date(r.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                            </td>
                            <td className="px-3 py-3 text-right">
                              <Button
                                onClick={async () => {
                                  try {
                                    await api.delete(`/admin/password-resets/${r.id}`);
                                    setPasswordResets((cur) => cur.filter((x) => x.id !== r.id));
                                    toast.success("Demande marquée comme transmise");
                                  } catch {
                                    toast.error("Échec de la suppression");
                                  }
                                }}
                                variant="outline"
                                size="sm"
                                className="rounded-none border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold"
                                data-testid={`password-reset-dismiss-${r.id}`}
                              >
                                Transmis
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>

              {/* Account events log (alerts) */}
              <Card className="rounded-none bg-white border border-gray-200 p-6 md:p-8" data-testid="events-log-card">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <Megaphone className="w-5 h-5 text-orange-600" />
                      <h2 className="font-heading font-bold text-2xl tracking-tight">Alertes comptes clients</h2>
                    </div>
                    <p className="text-sm text-gray-600">Suppressions &amp; réactivations récentes. Les alertes de plus de 7 jours sont supprimées automatiquement.</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {unreadCount > 0 && (
                      <Button onClick={markEventsRead} variant="outline" className="rounded-none border-black font-semibold" data-testid="admin-events-mark-read">
                        <CheckCircle2 className="w-4 h-4 mr-1.5" />
                        Tout marquer lu ({unreadCount})
                      </Button>
                    )}
                    {events.length > 0 && (
                      <Button
                        onClick={clearAllEvents}
                        variant="outline"
                        className="rounded-none border-red-600 text-red-700 hover:bg-red-50 font-semibold"
                        data-testid="admin-events-clear"
                      >
                        <Trash2 className="w-4 h-4 mr-1.5" />
                        Tout supprimer
                      </Button>
                    )}
                  </div>
                </div>
                {events.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-gray-300 bg-gray-50" data-testid="events-empty">
                    <Bell className="w-10 h-10 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 text-sm">Aucun événement enregistré.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-gray-100 border border-gray-200">
                    {events.map((ev) => {
                      const isDel = ev.type === "account_deleted";
                      return (
                        <li key={ev.id} className={`p-4 ${ev.read ? "" : "bg-amber-50/40"} hover:bg-gray-50`} data-testid={`event-row-${ev.id}`}>
                          <div className="flex items-start gap-3">
                            <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center ${isDel ? "bg-red-600 text-white" : "bg-emerald-600 text-white"}`}>
                              {isDel ? <UserCog className="w-4.5 h-4.5" /> : <CheckCircle2 className="w-4.5 h-4.5" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`text-xs font-bold px-2 py-0.5 uppercase tracking-wider ${isDel ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                                  {isDel ? "Désactivation" : "Réactivation"}
                                </span>
                                {!ev.read && <span className="text-[10px] font-bold px-1.5 bg-amber-400 text-amber-900">NOUVEAU</span>}
                                <span className="text-xs text-gray-400">{(ev.created_at || "").replace("T", " ").slice(0, 16)}</span>
                              </div>
                              <div className="text-sm font-semibold mt-1 text-gray-900 truncate">{ev.payload?.email}</div>
                              {ev.payload?.business_name && (
                                <div className="text-xs text-gray-600 truncate">{ev.payload.business_name}</div>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Logout all devices — confirmation dialog */}
      <Dialog open={logoutAllOpen} onOpenChange={setLogoutAllOpen}>
        <DialogContent className="rounded-none sm:max-w-md" data-testid="logout-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-700">
              <ShieldOff className="w-5 h-5" />
              Déconnecter tous les appareils
            </DialogTitle>
            <DialogDescription className="pt-2 text-gray-700">
              Cette action va révoquer <strong>toutes les sessions admin actives</strong>{" "}
              (Google SSO + SMS OTP) sur l'ensemble des appareils — y compris celui-ci.
              Vous devrez vous reconnecter immédiatement après. Utile en cas de perte de
              téléphone, d'activité suspecte ou de maintenance de sécurité.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 text-sm text-amber-900">
            Aucune autre donnée n'est touchée — clients, paiements, pages publiées et factures restent intacts.
          </div>
          <DialogFooter className="gap-2 mt-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-none"
              onClick={() => setLogoutAllOpen(false)}
              data-testid="logout-all-cancel"
            >
              Annuler
            </Button>
            <Button
              type="button"
              disabled={logoutAllLoading}
              onClick={async () => {
                setLogoutAllLoading(true);
                try {
                  const res = await api.post("/admin/auth/logout-all");
                  const n = res?.data?.sessions_revoked ?? 0;
                  toast.success(
                    n > 0
                      ? `${n} session(s) admin révoquée(s). Vous allez être redirigé.`
                      : "Toutes les sessions admin ont été révoquées."
                  );
                  setTimeout(() => {
                    logout();
                    nav("/admin/login");
                  }, 900);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Échec de la déconnexion globale.");
                  setLogoutAllLoading(false);
                }
              }}
              className="rounded-none bg-amber-600 hover:bg-amber-700 text-white font-semibold"
              data-testid="logout-all-confirm"
            >
              {logoutAllLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  Révocation…
                </>
              ) : (
                <>
                  <ShieldOff className="w-4 h-4 mr-1.5" />
                  Déconnecter tout
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
